import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Paper,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Checkbox,
  InputAdornment,
  Stack,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AirlineSeatReclineExtraIcon from "@mui/icons-material/AirlineSeatReclineExtra";
import BuildIcon from "@mui/icons-material/Build";
import DescriptionIcon from "@mui/icons-material/Description";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { Bucket } from "../../api/config/apiConfig";
import { imageUpload } from "../../api/services/services";
import gifLoading from "../../imgs/giphy.gif";

export const CAR_MEDIA_CATEGORIES = [
  {
    id: "all",
    label: "All Media",
    icon: <PhotoLibraryIcon fontSize="small" />,
    color: "#0284c7",
    bgColor: "#f0f9ff",
    badgeBg: "#e0f2fe",
    badgeColor: "#0369a1",
    borderColor: "#bae6fd",
    description: "All vehicle media, inspection docs & photos",
  },
  {
    id: "exterior",
    label: "Exterior",
    icon: <DirectionsCarIcon fontSize="small" />,
    color: "#0284c7",
    bgColor: "#f0f9ff",
    badgeBg: "#e0f2fe",
    badgeColor: "#0284c7",
    borderColor: "#bae6fd",
    description: "Front, rear, sides, grille, wheels & body",
  },
  {
    id: "interior",
    label: "Interior & Dash",
    icon: <AirlineSeatReclineExtraIcon fontSize="small" />,
    color: "#7c3aed",
    bgColor: "#f5f3ff",
    badgeBg: "#ede9fe",
    badgeColor: "#6d28d9",
    borderColor: "#ddd6fe",
    description: "Dashboard, steering, seats, odometer & console",
  },
  {
    id: "engine",
    label: "Engine Bay",
    icon: <BuildIcon fontSize="small" />,
    color: "#d97706",
    bgColor: "#fffbeb",
    badgeBg: "#fef3c7",
    badgeColor: "#b45309",
    borderColor: "#fde68a",
    description: "Engine, battery, mechanicals & underhood",
  },
  {
    id: "document",
    label: "Auction & Docs",
    icon: <DescriptionIcon fontSize="small" />,
    color: "#059669",
    bgColor: "#f0fdf4",
    badgeBg: "#d1fae5",
    badgeColor: "#047857",
    borderColor: "#a7f3d0",
    description: "Auction sheet, grading sheet & export certs",
  },
  {
    id: "condition",
    label: "Condition & Marks",
    icon: <ReportProblemIcon fontSize="small" />,
    color: "#e11d48",
    bgColor: "#fff1f2",
    badgeBg: "#ffe4e6",
    badgeColor: "#be123c",
    borderColor: "#fecdd3",
    description: "Scratches, dents, tire wear & imperfection marks",
  },
  {
    id: "other",
    label: "Accessories & Other",
    icon: <AutoAwesomeIcon fontSize="small" />,
    color: "#475569",
    bgColor: "#f8fafc",
    badgeBg: "#f1f5f9",
    badgeColor: "#334155",
    borderColor: "#e2e8f0",
    description: "Keys, accessories, booklets & extra attachments",
  },
];

// Helper to get category details
const getCategoryMeta = (catId) => {
  return (
    CAR_MEDIA_CATEGORIES.find((c) => c.id === catId) ||
    CAR_MEDIA_CATEGORIES[1] // fallback to exterior
  );
};

// Normalize image items (supports string key or object)
const normalizeImageItem = (img, idx = 0) => {
  if (!img) return null;
  if (typeof img === "string") {
    return {
      id: `img-${idx}-${img.slice(-10)}`,
      key: img,
      category: "exterior",
      caption: "",
      isCover: idx === 0,
      uploadedAt: null,
    };
  }
  const key = img.key || img.url || img.image || img.path || "";
  return {
    id: img.id || `img-${idx}-${(key || "").slice(-10)}`,
    key: key,
    category: img.category || "exterior",
    caption: img.caption || "",
    isCover: Boolean(img.isCover || (idx === 0 && img.isCover !== false)),
    uploadedAt: img.uploadedAt || null,
  };
};

/**
 * CarMediaUploaderViewer
 *
 * Props:
 * - images: Array of image keys or objects { key: string, category?: string, caption?: string, isCover?: boolean }
 * - onImagesChange: (newImages: Array) => void
 * - isEdit: boolean (whether upload/delete/edit controls are enabled)
 * - onNotify: (message: string, severity?: 'success'|'error'|'info') => void
 * - title: string (section title)
 * - subtitle: string (description)
 */
const CarMediaUploaderViewer = ({
  images = [],
  onImagesChange,
  isEdit = true,
  onNotify = () => {},
  title = "Car Media & Document Manager",
  subtitle = "Upload and organize high-res PNG & JPG photos by category (Exterior, Interior, Engine, Auction Sheets).",
}) => {
  const fileInputRef = useRef(null);

  // States
  const [activeCategory, setActiveCategory] = useState("all");
  const [uploadCategory, setUploadCategory] = useState("exterior");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // 'grid' | 'list'
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadingFile, setCurrentUploadingFile] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);

  // Category menu anchor
  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState(null);
  const [activeMenuImageKey, setActiveMenuImageKey] = useState(null);

  // Batch category menu anchor
  const [batchCategoryMenuAnchor, setBatchCategoryMenuAnchor] = useState(null);

  // Inline editing caption
  const [editingCaptionKey, setEditingCaptionKey] = useState(null);
  const [tempCaptionText, setTempCaptionText] = useState("");

  // Normalize image data
  const normalizedImages = useMemo(() => {
    return (images || [])
      .map((img, idx) => normalizeImageItem(img, idx))
      .filter(Boolean);
  }, [images]);

  // Sync uploadCategory when user switches activeCategory tab (if not 'all')
  const handleCategoryTabChange = (catId) => {
    setActiveCategory(catId);
    if (catId !== "all") {
      setUploadCategory(catId);
    }
  };

  // Category Counts
  const categoryCounts = useMemo(() => {
    const counts = { all: normalizedImages.length };
    CAR_MEDIA_CATEGORIES.forEach((cat) => {
      if (cat.id !== "all") {
        counts[cat.id] = normalizedImages.filter(
          (img) => img.category === cat.id
        ).length;
      }
    });
    return counts;
  }, [normalizedImages]);

  // Filtered Images
  const filteredImages = useMemo(() => {
    let list = normalizedImages;

    // Filter by active category tab
    if (activeCategory !== "all") {
      list = list.filter((img) => img.category === activeCategory);
    }

    // Filter by search query (filename or caption)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((img) => {
        const name = (img.key.split("/").pop() || "").toLowerCase();
        const cap = (img.caption || "").toLowerCase();
        const cat = (img.category || "").toLowerCase();
        return name.includes(q) || cap.includes(q) || cat.includes(q);
      });
    }

    return list;
  }, [normalizedImages, activeCategory, searchQuery]);

  // Lightbox slides
  const slides = useMemo(() => {
    return filteredImages.map((img, i) => {
      const url = img.key.startsWith("http") ? img.key : `${Bucket}${img.key}`;
      const catObj = getCategoryMeta(img.category);
      return {
        src: url,
        title: img.caption || `Photo #${i + 1}`,
        description: `Category: ${catObj.label} ${img.isCover ? "• Primary Cover Photo" : ""}`,
      };
    });
  }, [filteredImages]);

  // Drag & Drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEdit) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (!isEdit) return;

    const files = Array.from(e.dataTransfer.files || []);
    processFiles(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Upload processing
  const processFiles = async (files) => {
    const validFiles = files.filter((file) => {
      const isImage =
        file.type === "image/png" ||
        file.type === "image/jpeg" ||
        file.type === "image/jpg" ||
        file.type === "image/webp" ||
        /\.(png|jpe?g|webp)$/i.test(file.name);

      if (!isImage) {
        onNotify(`File "${file.name}" is not a valid PNG, JPG, or WEBP image`, "error");
      }
      return isImage;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(5);

    // Target category for this batch
    const targetCat =
      uploadCategory || (activeCategory !== "all" ? activeCategory : "exterior");

    const newUploadedObjects = [];
    let completedCount = 0;

    for (const file of validFiles) {
      setCurrentUploadingFile(file.name);
      try {
        const formData = new FormData();
        formData.append("image", file);

        const response = await imageUpload(formData);
        const key = response?.data?.data?.key;

        if (key) {
          // Clean filename for auto-caption
          const cleanCaption = file.name
            .replace(/\.[^/.]+$/, "")
            .replace(/[-_]/g, " ")
            .trim();

          newUploadedObjects.push({
            id: `img-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
            key: key,
            category: targetCat,
            caption: cleanCaption.length > 25 ? "" : cleanCaption,
            isCover: normalizedImages.length === 0 && newUploadedObjects.length === 0,
            uploadedAt: new Date().toISOString(),
          });
        }
      } catch (err) {
        console.error("Upload error for file", file.name, err);
        onNotify(`Failed to upload ${file.name}`, "error");
      } finally {
        completedCount++;
        setUploadProgress(
          Math.round(5 + (completedCount / validFiles.length) * 90)
        );
      }
    }

    setUploading(false);
    setUploadProgress(0);
    setCurrentUploadingFile("");

    if (newUploadedObjects.length > 0) {
      const catMeta = getCategoryMeta(targetCat);
      onNotify(
        `Uploaded ${newUploadedObjects.length} photo(s) to ${catMeta.label}!`,
        "success"
      );

      const updated = [...normalizedImages, ...newUploadedObjects];
      if (onImagesChange) {
        onImagesChange(updated);
      }
    }
  };

  // Change category of a single image
  const handleUpdateImageCategory = (imageKey, newCategory) => {
    const updated = normalizedImages.map((img) => {
      if (img.key === imageKey) {
        return { ...img, category: newCategory };
      }
      return img;
    });

    if (onImagesChange) {
      onImagesChange(updated);
    }

    const catMeta = getCategoryMeta(newCategory);
    onNotify(`Photo moved to "${catMeta.label}"`, "info");
    setCategoryMenuAnchor(null);
    setActiveMenuImageKey(null);
  };

  // Set cover photo
  const handleSetCoverPhoto = (imageKey) => {
    const target = normalizedImages.find((img) => img.key === imageKey);
    if (!target) return;

    // Set isCover to true for target, false for others, and move target to top
    const others = normalizedImages.filter((img) => img.key !== imageKey);
    const updated = [
      { ...target, isCover: true },
      ...others.map((img) => ({ ...img, isCover: false })),
    ];

    if (onImagesChange) {
      onImagesChange(updated);
    }
    onNotify("Photo set as primary vehicle cover!", "success");
  };

  // Reorder photo (move left or right)
  const handleMoveImage = (currentIndex, direction) => {
    const newIndex = direction === "left" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= filteredImages.length) return;

    const currentImg = filteredImages[currentIndex];
    const targetImg = filteredImages[newIndex];

    const currentGlobalIndex = normalizedImages.findIndex(
      (img) => img.key === currentImg.key
    );
    const targetGlobalIndex = normalizedImages.findIndex(
      (img) => img.key === targetImg.key
    );

    if (currentGlobalIndex === -1 || targetGlobalIndex === -1) return;

    const copy = [...normalizedImages];
    const temp = copy[currentGlobalIndex];
    copy[currentGlobalIndex] = copy[targetGlobalIndex];
    copy[targetGlobalIndex] = temp;

    if (onImagesChange) {
      onImagesChange(copy);
    }
  };

  // Delete image
  const handleDeleteImage = (targetKey) => {
    const updated = normalizedImages.filter((img) => img.key !== targetKey);
    if (onImagesChange) {
      onImagesChange(updated);
    }
    setDeleteConfirmIndex(null);
    setSelectedItems((prev) => prev.filter((k) => k !== targetKey));
    onNotify("Photo removed from vehicle library", "info");
  };

  // Inline caption save
  const handleSaveCaption = (imageKey) => {
    const updated = normalizedImages.map((img) => {
      if (img.key === imageKey) {
        return { ...img, caption: tempCaptionText.trim() };
      }
      return img;
    });

    if (onImagesChange) {
      onImagesChange(updated);
    }
    setEditingCaptionKey(null);
    setTempCaptionText("");
    onNotify("Caption updated", "success");
  };

  // Batch category change
  const handleBatchMoveCategory = (newCategory) => {
    if (selectedItems.length === 0) return;

    const updated = normalizedImages.map((img) => {
      if (selectedItems.includes(img.key)) {
        return { ...img, category: newCategory };
      }
      return img;
    });

    if (onImagesChange) {
      onImagesChange(updated);
    }

    const catMeta = getCategoryMeta(newCategory);
    onNotify(
      `Moved ${selectedItems.length} photos to "${catMeta.label}"`,
      "success"
    );
    setBatchCategoryMenuAnchor(null);
    setSelectedItems([]);
  };

  // Batch delete
  const handleBatchDelete = () => {
    if (selectedItems.length === 0) return;

    const updated = normalizedImages.filter(
      (img) => !selectedItems.includes(img.key)
    );
    if (onImagesChange) {
      onImagesChange(updated);
    }
    onNotify(`Deleted ${selectedItems.length} photo(s)`, "info");
    setSelectedItems([]);
  };

  // Toggle selection
  const handleToggleSelect = (key) => {
    setSelectedItems((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredImages.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredImages.map((img) => img.key));
    }
  };

  // Download
  const handleDownload = (imageUrl, filename) => {
    const url = imageUrl.startsWith("http") ? imageUrl : `${Bucket}${imageUrl}`;
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename || `vehicle-photo-${Date.now()}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch((_) => {
        window.open(url, "_blank");
      });
  };

  const handleCopyLink = (key) => {
    const url = key.startsWith("http") ? key : `${Bucket}${key}`;
    navigator.clipboard.writeText(url);
    onNotify("Image URL copied to clipboard!", "success");
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* SECTION HEADER & STATS */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          mb: 2.5,
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: "#0f172a",
              display: "flex",
              alignItems: "center",
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: "#e0f2fe",
                color: "#0284c7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <DirectionsCarIcon fontSize="small" />
            </Box>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
            {subtitle}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <Chip
            icon={<PhotoLibraryIcon fontSize="small" />}
            label={`${normalizedImages.length} Media Total`}
            color="primary"
            variant="outlined"
            sx={{
              fontWeight: 700,
              borderColor: "#bae6fd",
              bgcolor: "#f0f9ff",
              color: "#0369a1",
            }}
          />
          {isEdit && (
            <Button
              variant="contained"
              size="small"
              startIcon={<CloudUploadIcon />}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 700,
                px: 2,
                py: 0.7,
                background: "linear-gradient(135deg, #0284c7 0%, #0369a1 100%)",
                boxShadow: "0 4px 12px rgba(2, 132, 199, 0.2)",
              }}
            >
              Upload Photos
            </Button>
          )}
        </Box>
      </Box>

      {/* UPLOAD DROPZONE WITH CATEGORY SELECTOR (WHEN IN EDIT MODE) */}
      {isEdit && (
        <Paper
          elevation={0}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: isDragging ? "2px dashed #0284c7" : "2px dashed #cbd5e1",
            borderRadius: "18px",
            p: { xs: 2.5, md: 3.5 },
            textAlign: "center",
            backgroundColor: isDragging ? "#f0f9ff" : "#f8fafc",
            transition: "all 0.25s ease-in-out",
            mb: 3,
            position: "relative",
            "&:hover": {
              borderColor: "#0284c7",
              backgroundColor: "#f0f9ff",
            },
          }}
        >
          <input
            type="file"
            ref={fileInputRef}
            hidden
            multiple
            accept="image/png, image/jpeg, image/jpg, image/webp"
            onChange={handleFileSelect}
          />

          {/* Upload Target Category Picker */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 1,
              mb: 2.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#475569",
                mr: 0.5,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Upload Target Category:
            </Typography>

            {CAR_MEDIA_CATEGORIES.filter((c) => c.id !== "all").map((cat) => {
              const isSelected = uploadCategory === cat.id;
              return (
                <Chip
                  key={cat.id}
                  icon={cat.icon}
                  label={cat.label}
                  onClick={() => setUploadCategory(cat.id)}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    borderRadius: "8px",
                    height: "32px",
                    px: 1,
                    transition: "all 0.2s ease",
                    ...(isSelected
                      ? {
                          backgroundColor: cat.color,
                          color: "#ffffff",
                          boxShadow: `0 3px 10px ${cat.color}40`,
                          "& .MuiChip-icon": { color: "#ffffff" },
                          "&:hover": { backgroundColor: cat.color },
                        }
                      : {
                          backgroundColor: "#ffffff",
                          color: "#475569",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            borderColor: cat.color,
                            backgroundColor: cat.bgColor,
                            color: cat.color,
                          },
                        }),
                  }}
                />
              );
            })}
          </Box>

          {/* Upload Icon & Action */}
          <Box
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            sx={{ cursor: "pointer", py: 1 }}
          >
            <Box
              sx={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                backgroundColor: "#e0f2fe",
                color: "#0284c7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 12px",
                boxShadow: "0 4px 12px rgba(2, 132, 199, 0.15)",
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 28 }} />
            </Box>

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: "#1e293b", mb: 0.5 }}
            >
              Drag & drop car photos here, or{" "}
              <Box
                component="span"
                sx={{
                  color: "#0284c7",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                browse files
              </Box>
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", mb: 1 }}>
              Uploading to:{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  color: getCategoryMeta(uploadCategory).color,
                  bgcolor: getCategoryMeta(uploadCategory).badgeBg,
                  px: 1,
                  py: 0.3,
                  borderRadius: "6px",
                }}
              >
                {getCategoryMeta(uploadCategory).label}
              </Box>
              {" • "}
              Supports <b>PNG, JPG, JPEG, WEBP</b>
            </Typography>
          </Box>

          {/* Upload Progress Bar */}
          {uploading && (
            <Box
              sx={{
                width: "100%",
                maxWidth: "420px",
                mx: "auto",
                mt: 2,
                p: 2,
                bgcolor: "#ffffff",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "#0284c7",
                    maxWidth: "280px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Uploading: {currentUploadingFile || "Processing..."}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "#0f172a" }}
                >
                  {uploadProgress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e2e8f0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    background: "linear-gradient(90deg, #0284c7, #38bdf8)",
                  },
                }}
              />
            </Box>
          )}
        </Paper>
      )}

      {/* FILTER TABS & TOOLBAR */}
      <Paper
        elevation={0}
        sx={{
          p: 1.8,
          borderRadius: "14px",
          border: "1px solid #e2e8f0",
          backgroundColor: "#ffffff",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
          {/* Row 1: Category Tabs with Dynamic Count Badges (Horizontal scroll, never squished) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              overflowX: "auto",
              py: 0.5,
              width: "100%",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { height: "6px" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#cbd5e1", borderRadius: "10px" },
            }}
          >
            {CAR_MEDIA_CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat.id;
              const count = categoryCounts[cat.id] || 0;

              return (
                <Button
                  key={cat.id}
                  onClick={() => handleCategoryTabChange(cat.id)}
                  startIcon={cat.icon}
                  size="small"
                  sx={{
                    flexShrink: 0,
                    minWidth: "max-content",
                    textTransform: "none",
                    fontWeight: isActive ? 800 : 600,
                    borderRadius: "10px",
                    px: 1.8,
                    py: 0.8,
                    whiteSpace: "nowrap",
                    transition: "all 0.2s ease",
                    ...(isActive
                      ? {
                          backgroundColor: cat.color,
                          color: "#ffffff",
                          boxShadow: `0 4px 12px ${cat.color}35`,
                          "&:hover": { backgroundColor: cat.color },
                        }
                      : {
                          backgroundColor: "#f8fafc",
                          color: "#475569",
                          border: "1px solid #e2e8f0",
                          "&:hover": {
                            backgroundColor: cat.bgColor,
                            borderColor: cat.borderColor,
                            color: cat.color,
                          },
                        }),
                  }}
                >
                  {cat.label}
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      px: 0.8,
                      py: 0.1,
                      borderRadius: "12px",
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      backgroundColor: isActive
                        ? "rgba(255, 255, 255, 0.25)"
                        : cat.badgeBg,
                      color: isActive ? "#ffffff" : cat.badgeColor,
                    }}
                  >
                    {count}
                  </Box>
                </Button>
              );
            })}
          </Box>

          {/* Row 2: Search, Count & View Toggle */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              pt: 1,
              borderTop: "1px solid #f1f5f9",
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
              Showing {filteredImages.length} of {images.length} photos
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                ml: { xs: 0, sm: "auto" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <TextField
                size="small"
                placeholder="Search photos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery("")}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  width: { xs: "100%", sm: "220px" },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: "#f8fafc",
                    fontSize: "0.85rem",
                    "& fieldset": { borderColor: "#e2e8f0" },
                  },
                }}
              />

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, next) => next && setViewMode(next)}
                size="small"
                sx={{
                  borderRadius: "10px",
                  "& .MuiToggleButton-root": {
                    borderRadius: "8px !important",
                    p: 0.7,
                    border: "1px solid #e2e8f0",
                  },
                }}
              >
                <ToggleButton value="grid" aria-label="grid view">
                  <Tooltip title="Grid View">
                    <GridViewIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="list" aria-label="compact list view">
                  <Tooltip title="Compact View">
                    <ViewListIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>

        {/* BATCH ACTIONS BAR (WHEN 1 OR MORE IMAGES ARE CHECKED) */}
        {selectedItems.length > 0 && (
          <Fade in={selectedItems.length > 0}>
            <Box
              sx={{
                mt: 1.5,
                p: 1.2,
                px: 2,
                borderRadius: "10px",
                backgroundColor: "#0f172a",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Checkbox
                  checked={
                    selectedItems.length === filteredImages.length &&
                    filteredImages.length > 0
                  }
                  indeterminate={
                    selectedItems.length > 0 &&
                    selectedItems.length < filteredImages.length
                  }
                  onChange={handleSelectAll}
                  sx={{
                    color: "#94a3b8",
                    p: 0,
                    "&.Mui-checked": { color: "#38bdf8" },
                    "&.MuiCheckbox-indeterminate": { color: "#38bdf8" },
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {selectedItems.length} photo(s) selected
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isEdit && (
                  <>
                    <Button
                      size="small"
                      startIcon={<DriveFileMoveIcon />}
                      onClick={(e) => setBatchCategoryMenuAnchor(e.currentTarget)}
                      sx={{
                        color: "#ffffff",
                        bgcolor: "rgba(255, 255, 255, 0.15)",
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "8px",
                        fontSize: "0.78rem",
                        "&:hover": { bgcolor: "rgba(255, 255, 255, 0.25)" },
                      }}
                    >
                      Move Category ▾
                    </Button>

                    <Button
                      size="small"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={handleBatchDelete}
                      sx={{
                        color: "#fca5a5",
                        bgcolor: "rgba(239, 68, 68, 0.15)",
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "8px",
                        fontSize: "0.78rem",
                        "&:hover": { bgcolor: "rgba(239, 68, 68, 0.25)" },
                      }}
                    >
                      Delete Selected
                    </Button>
                  </>
                )}

                <IconButton
                  size="small"
                  onClick={() => setSelectedItems([])}
                  sx={{ color: "#94a3b8", "&:hover": { color: "#ffffff" } }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Fade>
        )}
      </Paper>

      {/* MEDIA GALLERY SECTION */}
      {filteredImages.length > 0 ? (
        viewMode === "grid" ? (
          /* GRID VIEW */
          <Grid container spacing={2.5}>
            {filteredImages.map((img, idx) => {
              const imageUrl = img.key.startsWith("http")
                ? img.key
                : `${Bucket}${img.key}`;
              const isSelected = selectedItems.includes(img.key);
              const catMeta = getCategoryMeta(img.category);
              const isCoverPhoto = Boolean(img.isCover);
              const isEditingCaption = editingCaptionKey === img.key;

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={img.key || idx}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: "16px",
                      border: isSelected
                        ? "2px solid #0284c7"
                        : isCoverPhoto
                        ? "2px solid #f59e0b"
                        : "1px solid #e2e8f0",
                      backgroundColor: "#ffffff",
                      position: "relative",
                      overflow: "hidden",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 12px 24px -4px rgba(15, 23, 42, 0.12)",
                        "& .overlay-actions": {
                          opacity: 1,
                          visibility: "visible",
                        },
                      },
                    }}
                  >
                    {/* Aspect Ratio Thumbnail Container */}
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: "200px",
                        backgroundColor: "#0f172a",
                        cursor: "pointer",
                        overflow: "hidden",
                      }}
                      onClick={() => setLightboxIndex(idx)}
                    >
                      <img
                        src={imageUrl}
                        alt={img.caption || `Car Photo ${idx + 1}`}
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = gifLoading;
                        }}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                          transition: "transform 0.4s ease",
                        }}
                      />

                      {/* Primary Cover Badge */}
                      {isCoverPhoto && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            bgcolor: "#f59e0b",
                            color: "#ffffff",
                            px: 1,
                            py: 0.3,
                            borderRadius: "6px",
                            fontWeight: 800,
                            fontSize: "0.7rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.4,
                            boxShadow: "0 2px 8px rgba(245, 158, 11, 0.4)",
                            zIndex: 2,
                          }}
                        >
                          <StarIcon sx={{ fontSize: 13 }} /> Cover Photo
                        </Box>
                      )}

                      {/* Category Badge (Top Left with Quick Change Click) */}
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isEdit) {
                            setActiveMenuImageKey(img.key);
                            setCategoryMenuAnchor(e.currentTarget);
                          }
                        }}
                        sx={{
                          position: "absolute",
                          top: 10,
                          left: 10,
                          bgcolor: catMeta.color,
                          color: "#ffffff",
                          px: 1,
                          py: 0.3,
                          borderRadius: "6px",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                          zIndex: 2,
                          cursor: isEdit ? "pointer" : "default",
                          "&:hover": isEdit
                            ? { filter: "brightness(1.15)" }
                            : {},
                        }}
                      >
                        {catMeta.icon}
                        <span>{catMeta.label}</span>
                        {isEdit && <span style={{ fontSize: "0.65rem" }}>▾</span>}
                      </Box>

                      {/* Selection Checkbox (Bottom overlay) */}
                      <Box
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          position: "absolute",
                          bottom: 10,
                          left: 10,
                          zIndex: 3,
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleToggleSelect(img.key)}
                          size="small"
                          sx={{
                            p: 0.3,
                            bgcolor: "rgba(15, 23, 42, 0.6)",
                            backdropFilter: "blur(4px)",
                            borderRadius: "6px",
                            color: "#ffffff",
                            "&.Mui-checked": {
                              bgcolor: "#0284c7",
                              color: "#ffffff",
                            },
                          }}
                        />
                      </Box>

                      {/* Hover Action Overlay */}
                      <Box
                        className="overlay-actions"
                        sx={{
                          position: "absolute",
                          inset: 0,
                          backgroundColor: "rgba(15, 23, 42, 0.65)",
                          backdropFilter: "blur(2px)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1.2,
                          opacity: 0,
                          visibility: "hidden",
                          transition: "all 0.25s ease-in-out",
                          zIndex: 2,
                        }}
                      >
                        <Tooltip title="Zoom / High-Res Preview" arrow>
                          <IconButton
                            size="small"
                            sx={{
                              backgroundColor: "#ffffff",
                              color: "#0284c7",
                              "&:hover": { backgroundColor: "#f0f9ff" },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setLightboxIndex(idx);
                            }}
                          >
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {isEdit && !isCoverPhoto && (
                          <Tooltip title="Set as Primary Cover Photo" arrow>
                            <IconButton
                              size="small"
                              sx={{
                                backgroundColor: "#ffffff",
                                color: "#f59e0b",
                                "&:hover": { backgroundColor: "#fffbeb" },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetCoverPhoto(img.key);
                              }}
                            >
                              <StarIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title="Download Photo" arrow>
                          <IconButton
                            size="small"
                            sx={{
                              backgroundColor: "#ffffff",
                              color: "#059669",
                              "&:hover": { backgroundColor: "#ecfdf5" },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(
                                imageUrl,
                                `${img.caption || "car-photo"}-${idx + 1}.jpg`
                              );
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Copy Direct S3 Link" arrow>
                          <IconButton
                            size="small"
                            sx={{
                              backgroundColor: "#ffffff",
                              color: "#475569",
                              "&:hover": { backgroundColor: "#f8fafc" },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyLink(img.key);
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {isEdit && (
                          <Tooltip title="Delete Photo" arrow>
                            <IconButton
                              size="small"
                              sx={{
                                backgroundColor: "#ffffff",
                                color: "#dc2626",
                                "&:hover": { backgroundColor: "#fef2f2" },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirmIndex(img.key);
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    {/* Card Content & Caption */}
                    <CardContent sx={{ p: 1.5, pb: "8px !important" }}>
                      {isEditingCaption ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <TextField
                            size="small"
                            autoFocus
                            value={tempCaptionText}
                            onChange={(e) => setTempCaptionText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveCaption(img.key);
                              if (e.key === "Escape") setEditingCaptionKey(null);
                            }}
                            placeholder="Add photo label/note..."
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                fontSize: "0.8rem",
                                py: 0.2,
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleSaveCaption(img.key)}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setEditingCaptionKey(null)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#1e293b",
                              fontSize: "0.85rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              flex: 1,
                              pr: 1,
                            }}
                          >
                            {img.caption || img.key.split("/").pop() || `Photo #${idx + 1}`}
                          </Typography>

                          {isEdit && (
                            <Tooltip title="Edit Caption / Note">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setEditingCaptionKey(img.key);
                                  setTempCaptionText(img.caption || "");
                                }}
                                sx={{ p: 0.3, color: "#94a3b8" }}
                              >
                                <EditIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      )}

                      {/* Card Reordering & Quick Info */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mt: 1,
                          pt: 1,
                          borderTop: "1px solid #f1f5f9",
                        }}
                      >
                        <Chip
                          label={`#${idx + 1}`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            height: "20px",
                            bgcolor: "#f1f5f9",
                            color: "#64748b",
                          }}
                        />

                        {isEdit && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Tooltip title="Move Left / Prioritize">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={idx === 0}
                                  onClick={() => handleMoveImage(idx, "left")}
                                  sx={{ p: 0.3, color: "#64748b" }}
                                >
                                  <ArrowBackIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title="Move Right">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={idx === filteredImages.length - 1}
                                  onClick={() => handleMoveImage(idx, "right")}
                                  sx={{ p: 0.3, color: "#64748b" }}
                                >
                                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          /* COMPACT LIST VIEW */
          <Stack spacing={1.5}>
            {filteredImages.map((img, idx) => {
              const imageUrl = img.key.startsWith("http")
                ? img.key
                : `${Bucket}${img.key}`;
              const isSelected = selectedItems.includes(img.key);
              const catMeta = getCategoryMeta(img.category);
              const isCoverPhoto = Boolean(img.isCover);

              return (
                <Paper
                  key={img.key || idx}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: "12px",
                    border: isSelected
                      ? "2px solid #0284c7"
                      : "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    transition: "all 0.2s ease",
                    "&:hover": { bgcolor: "#f8fafc" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggleSelect(img.key)}
                      size="small"
                    />

                    <Box
                      sx={{
                        width: 72,
                        height: 52,
                        borderRadius: "8px",
                        overflow: "hidden",
                        bgcolor: "#0f172a",
                        cursor: "pointer",
                        flexShrink: 0,
                      }}
                      onClick={() => setLightboxIndex(idx)}
                    >
                      <img
                        src={imageUrl}
                        alt={`Thumb ${idx + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: "#1e293b",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {img.caption || img.key.split("/").pop() || `Photo #${idx + 1}`}
                        </Typography>
                        {isCoverPhoto && (
                          <Chip
                            label="Cover"
                            size="small"
                            sx={{
                              bgcolor: "#fef3c7",
                              color: "#b45309",
                              fontWeight: 800,
                              fontSize: "0.68rem",
                              height: "18px",
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        Index #{idx + 1} • {img.key.split("/").pop()}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Category Pill with Quick Menu */}
                  <Chip
                    icon={catMeta.icon}
                    label={catMeta.label}
                    size="small"
                    onClick={(e) => {
                      if (isEdit) {
                        setActiveMenuImageKey(img.key);
                        setCategoryMenuAnchor(e.currentTarget);
                      }
                    }}
                    sx={{
                      bgcolor: catMeta.badgeBg,
                      color: catMeta.badgeColor,
                      fontWeight: 700,
                      cursor: isEdit ? "pointer" : "default",
                      "& .MuiChip-icon": { color: catMeta.badgeColor },
                    }}
                  />

                  {/* Actions */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title="View High-Res">
                      <IconButton
                        size="small"
                        onClick={() => setLightboxIndex(idx)}
                        sx={{ color: "#0284c7" }}
                      >
                        <ZoomInIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Download">
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDownload(
                            imageUrl,
                            `car-photo-${idx + 1}.jpg`
                          )
                        }
                        sx={{ color: "#059669" }}
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    {isEdit && (
                      <Tooltip title="Delete Photo">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirmIndex(img.key)}
                          sx={{ color: "#dc2626" }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        )
      ) : (
        /* EMPTY STATE */
        <Box
          sx={{
            py: 7,
            px: 3,
            textAlign: "center",
            backgroundColor: "#f8fafc",
            borderRadius: "18px",
            border: "1px dashed #cbd5e1",
          }}
        >
          <Box
            sx={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor: getCategoryMeta(activeCategory).bgColor,
              color: getCategoryMeta(activeCategory).color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
            }}
          >
            {getCategoryMeta(activeCategory).icon}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b" }}>
            No {activeCategory !== "all" ? getCategoryMeta(activeCategory).label : "vehicle"}{" "}
            media found
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#64748b", mt: 0.5, mb: 2.5, maxWidth: "480px", mx: "auto" }}
          >
            {isEdit
              ? `Select "${getCategoryMeta(activeCategory !== "all" ? activeCategory : "exterior").label}" above and drag photos here or click upload to add media.`
              : "No media files are attached in this category."}
          </Typography>

          {isEdit && (
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() => {
                if (activeCategory !== "all") setUploadCategory(activeCategory);
                fileInputRef.current && fileInputRef.current.click();
              }}
              sx={{
                textTransform: "none",
                borderRadius: "10px",
                fontWeight: 700,
                color: "#0284c7",
                borderColor: "#0284c7",
                "&:hover": {
                  borderColor: "#0369a1",
                  backgroundColor: "#f0f9ff",
                },
              }}
            >
              Upload {activeCategory !== "all" ? getCategoryMeta(activeCategory).label : "Car"} Photos
            </Button>
          )}
        </Box>
      )}

      {/* SINGLE IMAGE CATEGORY CHANGE MENU */}
      <Menu
        anchorEl={categoryMenuAnchor}
        open={Boolean(categoryMenuAnchor)}
        onClose={() => {
          setCategoryMenuAnchor(null);
          setActiveMenuImageKey(null);
        }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.2)",
            minWidth: "200px",
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{ px: 2, py: 1, display: "block", color: "#64748b", fontWeight: 700 }}
        >
          Change Category
        </Typography>
        <Divider />
        {CAR_MEDIA_CATEGORIES.filter((c) => c.id !== "all").map((cat) => (
          <MenuItem
            key={cat.id}
            onClick={() => handleUpdateImageCategory(activeMenuImageKey, cat.id)}
            sx={{
              py: 1,
              "&:hover": { backgroundColor: cat.bgColor },
            }}
          >
            <ListItemIcon sx={{ color: cat.color, minWidth: "32px" }}>
              {cat.icon}
            </ListItemIcon>
            <ListItemText
              primary={cat.label}
              primaryTypographyProps={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "#1e293b",
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* BATCH CATEGORY CHANGE MENU */}
      <Menu
        anchorEl={batchCategoryMenuAnchor}
        open={Boolean(batchCategoryMenuAnchor)}
        onClose={() => setBatchCategoryMenuAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(15, 23, 42, 0.2)",
            minWidth: "220px",
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{ px: 2, py: 1, display: "block", color: "#64748b", fontWeight: 700 }}
        >
          Move {selectedItems.length} Photos To:
        </Typography>
        <Divider />
        {CAR_MEDIA_CATEGORIES.filter((c) => c.id !== "all").map((cat) => (
          <MenuItem
            key={cat.id}
            onClick={() => handleBatchMoveCategory(cat.id)}
            sx={{
              py: 1,
              "&:hover": { backgroundColor: cat.bgColor },
            }}
          >
            <ListItemIcon sx={{ color: cat.color, minWidth: "32px" }}>
              {cat.icon}
            </ListItemIcon>
            <ListItemText
              primary={cat.label}
              primaryTypographyProps={{
                fontSize: "0.85rem",
                fontWeight: 600,
                color: "#1e293b",
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* DELETE CONFIRMATION MODAL */}
      <Dialog
        open={Boolean(deleteConfirmIndex)}
        onClose={() => setDeleteConfirmIndex(null)}
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#0f172a" }}>
          Delete Vehicle Photo?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Are you sure you want to delete this photo from the vehicle profile?
            This will update the vehicle media library.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteConfirmIndex(null)}
            sx={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDeleteImage(deleteConfirmIndex)}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              px: 2.5,
            }}
          >
            Delete Photo
          </Button>
        </DialogActions>
      </Dialog>

      {/* HIGH-RESOLUTION INTERACTIVE LIGHTBOX VIEWER */}
      <Lightbox
        open={lightboxIndex >= 0}
        index={lightboxIndex}
        close={() => setLightboxIndex(-1)}
        slides={slides}
        carousel={{ finite: slides.length <= 1 }}
        render={{
          buttonPrev: slides.length <= 1 ? () => null : undefined,
          buttonNext: slides.length <= 1 ? () => null : undefined,
        }}
      />
    </Box>
  );
};

export default CarMediaUploaderViewer;
